import { PlantDataFormStep } from '@/types/forms/formSteps/plantDataFormStep';
import { ApplicationForm } from '@/types/forms/specific/applicationForm';
import {
    isStorageCouplingAcCoupled,
    isStorageCouplingDcCoupled,
    isStorageConnectionConceptWithoutPowerGeneration,
    isStorageConnectionConceptWithoutPowerGenerationAndWithConsumptionPlant,
    isStorageConnectionConceptWithPowerGeneration,
    isStorageConnectionConceptStorageInGenerationPath,
    isStorageConnectionConceptStorageInConsumerPath,
    isStorageChargeFromPublicGrid,
    isStorageDischargeIntoPublicGrid,
    isPluginGeneratingPlant,
    isPv,
    isMediumVoltage,
    isStorage,
    isStorageDischargeCustomersGridAndPublicGrid,
    isRequestWithoutPlanning,
    isRequestWithPlanning,
    isPvAndStorageLte30,
    isHighEfficiencyChpPlant,
    isTotalPlantPerformanceAbove300,
    isDischargePowerAboveTippingPoint,
} from './common';
import { plantDataInfeedManagementRules } from './plantData/infeedManagement';
import { getOverallNominalPower } from '../../utilities/application/plantPerformance';
import { toggleRequiredValidator } from '@/utilities/toggleRequiredValidator';

export const plantDataRules = async (
    formStep: { model: PlantDataFormStep; formModel: ApplicationForm } & Vue,
): Promise<void> => {
    const applicationType = formStep.formModel.applicationType;
    const hasStorage = isStorage(formStep.formModel);

    formStep.model.newOrExistingApplication.disabled =
        applicationType == 'photovoltaicAndStorageLte30' ||
        (applicationType === 'storage' &&
            isRequestWithoutPlanning(formStep.formModel));
    formStep.model.plantWasAlreadyCommissioned.disabled =
        applicationType === 'storage' &&
        isRequestWithoutPlanning(formStep.formModel);

    formStep.model.applicationNumber.disabled =
        formStep.model.newOrExistingApplication.disabled ||
        formStep.model.newOrExistingApplication.value !== 'change';

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-newOrExistingPlant
    {
        if (
            formStep.formModel.applicationType == 'photovoltaicAndStorageLte30'
        ) {
            formStep.model.newOrExistingPlant.value = 'first';
        }

        formStep.model.newOrExistingPlant.disabled =
            applicationType === 'photovoltaic' ||
            applicationType == 'photovoltaicAndStorageLte30' ||
            (applicationType === 'storage' &&
                isRequestWithoutPlanning(formStep.formModel));

        for (const option of formStep.model.newOrExistingPlant.options) {
            switch (option.key) {
                case 'performanceIncrease':
                    option.disabled = hasStorage;
                    break;
                case 'capacityIncrease':
                case 'capacityIncreaseWithNominalDischargeCapacityChange':
                    option.disabled = !hasStorage;
            }
        }
    }

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-powerHeatCouplingPlantType
    formStep.model.powerHeatCouplingPlantType.disabled =
        applicationType !== 'powerHeatCoupling';

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-powerHeatCouplingPaymentsBasedOnLawHint
    formStep.model.powerHeatCouplingPaymentsBasedOnLawHint.disabled =
        formStep.model.powerHeatCouplingPlantType.disabled ||
        formStep.model.powerHeatCouplingPlantType.value !==
            'highEfficiencyChpPlant';

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-bafaApprovalWillBeSubmittedLater
    formStep.model.bafaApprovalWillBeSubmittedLater.disabled =
        formStep.model.powerHeatCouplingPlantType.disabled ||
        formStep.model.powerHeatCouplingPlantType.value !==
            'highEfficiencyChpPlant';

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-initialCommissioningDate
    formStep.model.initialCommissioningDate.disabled =
        formStep.model.plantWasAlreadyCommissioned.value !== true;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-plantBuildingType
    {
        formStep.model.plantBuildingType.disabled =
            formStep.formModel.generationType.plantStructureType.disabled ||
            formStep.formModel.generationType.plantStructureType.value !==
                'building' ||
            (isPv(formStep.formModel) &&
                (formStep.formModel.plantPerformance.photovoltaic
                    .pvOverallNominalPower.value ?? 0) > 100) ||
            isMediumVoltage(formStep.formModel.powerConnection);
    }
    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-plantPosition
    formStep.model.plantPosition.disabled =
        formStep.model.plantBuildingType.disabled ||
        formStep.model.plantBuildingType.value !== 'noResidentialBuilding';

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-pvInstallationType
    {
        formStep.model.pvInstallationType.disabled =
            (applicationType !== 'photovoltaic' &&
                applicationType !== 'photovoltaicAndStorageLte30') ||
            (formStep.formModel.plantPerformance.photovoltaic
                .pvOverallNominalPower.value ?? 0) > 100 ||
            isMediumVoltage(formStep.formModel.powerConnection);

        for (const option of formStep.model.pvInstallationType.options) {
            switch (option.key) {
                case 'residentialOrSimilarBuilding':
                    option.disabled =
                        (formStep.formModel.generationType.plantStructureType
                            .disabled ||
                            formStep.formModel.generationType.plantStructureType
                                .value !== 'noiseBarrier') &&
                        (formStep.model.plantBuildingType.disabled ||
                            formStep.model.plantBuildingType.value !==
                                'residentialBuilding') &&
                        (formStep.model.plantPosition.disabled ||
                            formStep.model.plantPosition.value) !== 'indoor';
                    break;

                case 'outsideNonResidentialBuilding':
                    option.disabled =
                        formStep.model.plantPosition.disabled ||
                        formStep.model.plantPosition.value !== 'outdoor';
                    break;
                case 'outsideNonResidentialBuildingEEG48Abs1':
                    option.disabled =
                        formStep.model.plantPosition.disabled ||
                        formStep.model.plantPosition.value !== 'outdoor';
                    break;

                case 'structuralFacility':
                case 'nextToResidentialBuilding':
                case 'publicWasteDisposalFacilities':
                case 'approvedDevelopmentPlan':
                case 'agriculturalLandOrParkingLots':
                case 'artificialWaterBody':
                case 'drainedPeatland':
                    option.disabled =
                        formStep.formModel.generationType.disabled ||
                        formStep.formModel.generationType.plantStructureType
                            .value !== 'openSpacePlant';
                    break;
            }
        }
    }

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-pvInstallationTypeForAgriculturalLandOrParkingLots
    formStep.model.pvInstallationTypeForAgriculturalLandOrParkingLots.disabled =
        applicationType !== 'photovoltaic' ||
        formStep.model.pvInstallationType.disabled ||
        formStep.model.pvInstallationType.value !==
            'agriculturalLandOrParkingLots';

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-storageConnection
    // The field is only displayed if it is an application with storage.
    // and this application has planning maturity.
    formStep.model.storageConnection.disabled = !(
        hasStorage && !isRequestWithoutPlanning(formStep.formModel)
    );
    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-storageCoupling
    {
        formStep.model.storageCoupling.disabled = !hasStorage;
    }
    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-storageConnectionConcept
    {
        formStep.model.storageConnectionConcept.disabled =
            !hasStorage || !formStep.model.storageCoupling.value;

        const storageCouplingIsAc = isStorageCouplingAcCoupled(formStep.model);
        const storageCouplingIsDc = isStorageCouplingDcCoupled(formStep.model);
        for (const option of formStep.model.storageConnectionConcept.options) {
            switch (option.key) {
                case 'storageWithoutPowerGenerationPlantAndWithoutConsumptionPlant':
                case 'storageWithoutPowerGenerationPlantAndWithConsumptionPlant':
                    option.disabled = !storageCouplingIsAc;
                    break;
                // ED-838: also available for DC coupling, but not for plug-in plants
                case 'storageFacilityWithPowerGenerationPlantAndWithoutConsumptionPlant':
                    option.disabled = !(
                        storageCouplingIsAc ||
                        (storageCouplingIsDc &&
                            !isPluginGeneratingPlant(formStep.formModel))
                    );
                    break;
                case 'powerGenerationPlantWithStorageInTheConsumerPathAndConsumptionPlant':
                    option.disabled = !storageCouplingIsAc;
                    break;

                case 'powerGenerationPlantWithStorageFacilityInTheGenerationPathAndConsumptionPlant':
                    option.disabled = !storageCouplingIsDc;
                    break;
            }
        }
    }

    // Enable or disable all storageCoupling options based on the value of storageHasSeparateInverter
    // For normal storage applications, the options are always enabled.
    // For photovoltaicAndStorageLte30 applications, the options need to be set to acCoupled or dcCoupled depending on the value of storageHasSeparateInverter.
    const isStorageHasSeparateInverterEnabled = !formStep.formModel
        .plantPerformance.storageHasSeparateInverter.disabled;
    formStep.model.storageCoupling.options.forEach((option) => {
        option.disabled = isStorageHasSeparateInverterEnabled;
    });
    formStep.model.storageCoupling.userChangeable = !isStorageHasSeparateInverterEnabled;
    if (isStorageHasSeparateInverterEnabled) {
        formStep.model.storageCoupling.options.forEach((option) => {
            if (option.key === 'acCoupled') {
                option.disabled = !formStep.formModel.plantPerformance
                    .storageHasSeparateInverter.value;
            } else if (option.key === 'dcCoupled') {
                option.disabled = !!formStep.formModel.plantPerformance
                    .storageHasSeparateInverter.value;
            }
        });
    }

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-electricityTaxExemptionHint
    formStep.model.electricityTaxExemptionHint.disabled = hasStorage;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-tenantElectricitySurcharge
    formStep.model.tenantElectricitySurcharge.disabled =
        (applicationType !== 'photovoltaic' &&
            applicationType !== 'photovoltaicAndStorageLte30') ||
        isPluginGeneratingPlant(formStep.formModel) ||
        formStep.formModel.generationType.plantStructureType.value !==
            'building';

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-requirementTenantElectricitySurchargeHint
    formStep.model.requirementTenantElectricitySurchargeHint.disabled =
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value;

    formStep.model.plantBuildingTypeForTenantElectricitySurcharge.disabled =
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value ||
        !formStep.model.plantBuildingType.disabled ||
        (isPv(formStep.formModel) &&
            (formStep.formModel.plantPerformance.photovoltaic
                .pvOverallNominalPower.value ?? 0) <= 100);

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-minimumRequirementLivingSpace
    formStep.model.minimumRequirementLivingSpace.disabled =
        (formStep.model.plantBuildingTypeForTenantElectricitySurcharge.value ==
            'noResidentialBuilding' &&
            !formStep.model.plantBuildingTypeForTenantElectricitySurcharge
                .disabled) ||
        (formStep.model.plantBuildingType.value == 'noResidentialBuilding' &&
            !formStep.model.plantBuildingType.disabled) ||
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value;

    formStep.model.noResidentialExemptions.disabled =
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value == true ||
        (formStep.model.plantBuildingTypeForTenantElectricitySurcharge.value !==
            'noResidentialBuilding' &&
            !formStep.model.plantBuildingTypeForTenantElectricitySurcharge
                .disabled) ||
        (formStep.model.plantBuildingType.value !== 'noResidentialBuilding' &&
            !formStep.model.plantBuildingType.disabled);

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-electricityConsumptionWithinThisBuilding
    formStep.model.electricityConsumptionWithinThisBuilding.disabled =
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-noAdditionalNetworkForEndConsumer
    formStep.model.noAdditionalNetworkForEndConsumer.disabled =
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-federalNetworkAgencyHint
    formStep.model.federalNetworkAgencyHint.disabled =
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-proofOfRegistrationPhotovoltaic
    formStep.model.proofOfRegistrationPhotovoltaic.disabled =
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-proofOfAllocationOfTenantElectricitySurcharge
    formStep.model.proofOfAllocationOfTenantElectricitySurcharge.disabled =
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-declarationEnwgHint
    formStep.model.declarationEnwgHint.disabled =
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-plantOperatorKnowsEnwgSpecifications
    formStep.model.plantOperatorKnowsEnwgSpecifications.disabled =
        formStep.model.tenantElectricitySurcharge.disabled ||
        !formStep.model.tenantElectricitySurcharge.value;

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-storageCharge
    {
        formStep.model.storageCharge.disabled =
            !hasStorage || !formStep.model.storageCoupling.value;

        const isSCCWithoutPowerGenerationAndWithOrWithoutConsumptionPlant =
            isStorageConnectionConceptWithoutPowerGeneration(formStep.model) ||
            isStorageConnectionConceptWithoutPowerGenerationAndWithConsumptionPlant(
                formStep.model,
            );
        for (const option of formStep.model.storageCharge.options) {
            switch (option.key) {
                case 'onlyFromEeg':
                    option.disabled = isSCCWithoutPowerGenerationAndWithOrWithoutConsumptionPlant;
                    break;
                case 'onlyFromKwkg':
                case 'fromEegAndKwkg':
                case 'fromPublicGridAndEeg':
                case 'fromPublicGridAndKwkg':
                case 'fromPublicGridAndEegAndKwkg':
                    option.disabled = isSCCWithoutPowerGenerationAndWithOrWithoutConsumptionPlant;
                    break;

                case 'onlyFromPublicGrid':
                    option.disabled = !isSCCWithoutPowerGenerationAndWithOrWithoutConsumptionPlant;
                    break;
            }
        }
    }

    // realizationDeposit — Realisierungskaution Hinweis
    formStep.model.realizationDeposit.disabled = !(
        isTotalPlantPerformanceAbove300(formStep.formModel) &&
        isRequestWithPlanning(formStep.formModel) &&
        isDischargePowerAboveTippingPoint(
            formStep.formModel.plantPerformance,
            2000,
        ) &&
        isStorageChargeFromPublicGrid(formStep.model)
    );

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-storageDischarge
    {
        formStep.model.storageDischarge.disabled =
            !hasStorage || !formStep.model.storageCoupling.value;

        for (const option of formStep.model.storageDischarge.options) {
            switch (option.key) {
                case 'onlyIntoCustomersGridSelfSupply':
                case 'intoCustomersGridAndPublicGridBivalent':
                    option.disabled =
                        !isStorageConnectionConceptStorageInGenerationPath(
                            formStep.model,
                        ) &&
                        !isStorageConnectionConceptStorageInConsumerPath(
                            formStep.model,
                        ) &&
                        !isStorageConnectionConceptWithoutPowerGenerationAndWithConsumptionPlant(
                            formStep.model,
                        );
                    break;

                case 'onlyIntoPublicGridGridFeedIn':
                    option.disabled =
                        !isStorageConnectionConceptWithoutPowerGeneration(
                            formStep.model,
                        ) &&
                        !isStorageConnectionConceptWithPowerGeneration(
                            formStep.model,
                        );
                    break;
            }
        }
    }
    const isEligibleApplicationType =
        formStep.formModel.applicationType === 'photovoltaicAndStorageLte30' ||
        formStep.formModel.applicationType === 'dea' ||
        formStep.formModel.applicationType === 'photovoltaic' ||
        (formStep.formModel.applicationType === 'powerHeatCoupling' &&
            isHighEfficiencyChpPlant(formStep.model));

    const overallNominalPower =
        getOverallNominalPower(formStep.formModel.plantPerformance) || 0;

    const isPlugin = isPluginGeneratingPlant(formStep.formModel);

    formStep.model.activePowerLimit60.disabled =
        !(
            overallNominalPower > 2 &&
            overallNominalPower < 100 &&
            isEligibleApplicationType
        ) ||
        isPlugin ||
        formStep.formModel.applicationType === 'storage';

    const storageChargeFromPublicGrid = isStorageChargeFromPublicGrid(
        formStep.model,
    );

    const storageDischargeIntoPublicGrid = isStorageDischargeIntoPublicGrid(
        formStep.model,
    );

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-storageBalancingEnergyMarketParticipation
    formStep.model.storageBalancingEnergyMarketParticipation.disabled =
        !hasStorage ||
        (!storageChargeFromPublicGrid && !storageDischargeIntoPublicGrid);

    // https://netzkern.atlassian.net/wiki/spaces/EWE/pages/4868800911/Formular+Anlagendaten#FormularAnlagendaten-storageBalancingEnergyMarketParticipationType
    {
        formStep.model.storageBalancingEnergyMarketParticipationType.disabled =
            formStep.model.storageBalancingEnergyMarketParticipation.disabled ||
            !formStep.model.storageBalancingEnergyMarketParticipation.value;

        for (const option of formStep.model
            .storageBalancingEnergyMarketParticipationType.options) {
            switch (option.key) {
                case 'positiveAndNegative':
                    option.disabled =
                        !storageChargeFromPublicGrid ||
                        !storageDischargeIntoPublicGrid;
                    break;

                case 'onlyPositive':
                    option.disabled = !storageDischargeIntoPublicGrid;
                    break;

                case 'onlyNegative':
                    option.disabled = !storageChargeFromPublicGrid;
                    break;
            }
        }
    }

    formStep.model.storagePublicDischargeWarning.disabled =
        !hasStorage ||
        !(
            (isStorageConnectionConceptStorageInGenerationPath(
                formStep.model,
            ) ||
                isStorageConnectionConceptStorageInConsumerPath(
                    formStep.model,
                )) &&
            isStorageDischargeCustomersGridAndPublicGrid(formStep.model)
        );

    await plantDataInfeedManagementRules(formStep);
};
